import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

import { StartFreeButton } from "./MarketingButton";
import { marketingButtonClass } from "./marketingButtonClass";
import { scrollToSection } from "./scrollToSection";

type ProductHeroProps = {
  title: ReactNode;
  lede: ReactNode;
  cta: string;
  secondaryLabel?: string;
  proof?: string[];
  stage?: ReactNode;
  /** Gives the stage the wider column, for stages with several items. */
  wideStage?: boolean;
  /** Color of the curved edge at the bottom, matching the next section. */
  curveClassName?: string;
  /** Hides the decorative ring behind the stage. */
  plainStage?: boolean;
  /** Full-bleed photo behind the copy, instead of a stage. */
  photo?: string;
  className?: string;
};

export default function ProductHero({
  title,
  lede,
  cta,
  secondaryLabel = "See how it works ↓",
  proof = [],
  stage,
  wideStage = false,
  curveClassName = "after:bg-white",
  plainStage = false,
  photo,
  className,
}: ProductHeroProps) {
  return (
    <section
      style={photo ? { backgroundImage: `url(${photo})` } : undefined}
      className={cn(
        "relative min-h-[680px] overflow-hidden px-[6vw] pb-[120px] pt-[90px] text-white",
        photo
          ? "bg-cover bg-center"
          : "[background:radial-gradient(circle_at_82%_18%,rgba(71,88,255,0.46),transparent_28%),radial-gradient(circle_at_68%_74%,rgba(136,55,240,0.26),transparent_30%),linear-gradient(135deg,#060b2c_0%,#07123f_60%,#03071e_100%)]",
        "before:pointer-events-none before:absolute before:inset-0 before:content-['']",
        photo
          ? "before:[background:linear-gradient(90deg,rgba(2,6,28,0.94)_0%,rgba(2,6,28,0.78)_46%,rgba(2,6,28,0.38)_75%,rgba(2,6,28,0.7)_100%)]"
          : "before:opacity-[0.18] before:[background-image:radial-gradient(rgba(255,255,255,0.55)_0.7px,transparent_0.7px)] before:[background-size:24px_24px] before:[mask-image:linear-gradient(to_right,transparent,black_45%,black)]",
        "after:absolute after:-bottom-[55px] after:-left-[3%] after:-right-[3%] after:h-[105px] after:rounded-[50%_50%_0_0/72%_72%_0_0] after:content-['']",
        curveClassName,
        "max-[980px]:px-[5vw] max-[980px]:pb-[110px] max-[980px]:pt-[70px] max-[680px]:min-h-0 max-[680px]:px-5 max-[680px]:pb-[102px] max-[680px]:pt-16",
        className,
      )}
    >
      <div
        className={cn(
          "relative z-[1] mx-auto grid w-[min(1220px,100%)] items-center max-[980px]:grid-cols-1",
          photo
            ? "grid-cols-1"
            : wideStage
              ? "grid-cols-[0.92fr_1.08fr]"
              : "grid-cols-[1.02fr_0.98fr]",
          wideStage ? "gap-12" : "gap-[68px] max-[980px]:gap-[42px]",
        )}
      >
        <div
          className={
            wideStage
              ? "max-w-[610px]"
              : photo
                ? "max-w-[780px] max-[980px]:max-w-[800px]"
                : "max-w-[680px] max-[980px]:max-w-[800px]"
          }
        >
          <h1 className="m-0 font-figtree text-[clamp(48px,6vw,84px)] font-black uppercase leading-[0.97] tracking-[-0.055em] [text-wrap:balance] max-[680px]:text-[clamp(44px,13vw,66px)]">
            {title}
          </h1>
          <p className="mb-[31px] mt-[25px] max-w-[690px] font-open-sans text-[clamp(18px,1.75vw,22px)] leading-[1.55] text-white/[0.82] [text-wrap:balance] max-[680px]:text-[18px]">
            {lede}
          </p>
          <div className="flex flex-wrap items-center gap-[15px]">
            <StartFreeButton>{cta}</StartFreeButton>
            <a
              href="#how-it-works"
              onClick={(e) => {
                e.preventDefault();
                scrollToSection("how-it-works");
              }}
              className={marketingButtonClass("secondary")}
            >
              {secondaryLabel}
            </a>
          </div>
          {proof.length > 0 && (
            <div className="mt-[30px] flex gap-[22px] font-open-sans text-[14px] text-white/70 max-[680px]:flex-col max-[680px]:gap-[10px]">
              {proof.map((item) => (
                <span key={item} className="flex items-center gap-2">
                  <i className="grid size-[18px] place-items-center rounded-full bg-teal-500 text-[11px] font-black not-italic text-marketing-navy">
                    ✓
                  </i>
                  {item}
                </span>
              ))}
            </div>
          )}
        </div>
        {stage && (
          <div
            className={cn(
              "relative grid place-items-center max-[980px]:min-h-[440px]",
              wideStage
                ? "min-h-[470px] max-[680px]:min-h-[320px]"
                : "min-h-[500px]",
              !plainStage &&
                "before:absolute before:size-[520px] before:rounded-full before:border before:border-[rgba(144,157,255,0.22)] before:shadow-[0_0_0_64px_rgba(57,73,219,0.06),0_0_0_128px_rgba(57,73,219,0.03)] before:content-[''] max-[680px]:before:size-[360px]",
            )}
          >
            {stage}
          </div>
        )}
      </div>
    </section>
  );
}

type ProductShotProps = {
  src: string;
  alt: string;
  caption?: string;
  tall?: boolean;
  className?: string;
  imgClassName?: string;
};

/** A real product screenshot in a frosted frame, slightly tilted. */
export function ProductShot({
  src,
  alt,
  caption,
  tall = false,
  className,
  imgClassName,
}: ProductShotProps) {
  return (
    <figure
      className={cn(
        "relative z-[1] m-0 rounded-[25px] bg-white/[0.12] p-[10px] shadow-[0_30px_90px_rgba(0,0,0,0.46)] backdrop-blur-[12px] max-[680px]:rotate-0",
        tall
          ? "w-[min(390px,80%)] rotate-[1.5deg]"
          : "w-[min(650px,100%)] rotate-1",
        className,
      )}
    >
      <img
        src={src}
        alt={alt}
        className={cn(
          "w-full rounded-[18px] bg-white",
          tall
            ? "max-h-[520px] object-contain"
            : "max-h-[490px] object-cover object-[top_left]",
          imgClassName,
        )}
      />
      {caption && (
        <figcaption className="px-3 pb-[2px] pt-[10px] font-open-sans text-[13px] font-bold text-white/[0.74]">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}
